/**
 * useTokenBalance — reads ERC-20 token balance and native (ETH/A0GI) balance.
 *
 * Uses publicClient.readContract for ERC-20 balanceOf and publicClient.getBalance
 * for native currency. Auto-refreshes every 15 seconds.
 *
 * ARCH-27: Contract interaction hook pattern (useQuery with polling)
 * Story 5.2 Task 1
 */

import { useQuery } from "@tanstack/react-query";
import { formatUnits } from "viem";
import { getPublicClient } from "wagmi/actions";

import { erc20Abi } from "../../lib/abis/erc20";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { useWallet } from "../use-wallet";

type BalanceData = {
	balance: bigint;
	nativeBalance: bigint;
};

function isHex(value: string): value is `0x${string}` {
	return value.startsWith("0x");
}

function toHex(value: string): `0x${string}` {
	const lower = value.toLowerCase();
	if (!isHex(lower)) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error(`Invalid hex address: ${value}`);
	}
	return lower;
}

async function fetchBalances(tokenAddress: string, walletAddress: string): Promise<BalanceData> {
	const activeChain = getActiveChain();
	const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });

	if (!publicClient) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error("Public client unavailable");
	}

	const tokenHex = toHex(tokenAddress);
	const walletHex = toHex(walletAddress);

	const [balance, nativeBalance] = await Promise.all([
		publicClient.readContract({
			address: tokenHex,
			abi: erc20Abi,
			functionName: "balanceOf",
			args: [walletHex],
		}),
		publicClient.getBalance({
			address: walletHex,
		}),
	]);

	return { balance, nativeBalance };
}

function formatBalance(balance: bigint, decimals: number): string {
	const raw = formatUnits(balance, decimals);
	// Strip trailing zeros after decimal point for clean display
	if (raw.includes(".")) {
		const trimmed = raw.replace(/0+$/, "").replace(/\.$/, "");
		return trimmed || "0";
	}
	return raw;
}

type UseTokenBalanceReturn = {
	balance: bigint | undefined;
	nativeBalance: bigint | undefined;
	formatted: string;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
};

/**
 * Hook to read ERC-20 token balance and native currency balance.
 *
 * @param tokenAddress - The ERC-20 token contract address (undefined to disable)
 * @param decimals - Token decimals (default 18)
 * @returns Balance data with formatted display, loading/error states
 */
export function useTokenBalance(
	tokenAddress: string | undefined,
	decimals = 18
): UseTokenBalanceReturn {
	const { address: walletAddress, isConnected } = useWallet();

	const enabled = Boolean(tokenAddress) && Boolean(walletAddress) && isConnected;

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["token-balance", tokenAddress, walletAddress],
		queryFn: () => fetchBalances(tokenAddress ?? "", walletAddress ?? ""),
		enabled,
		refetchInterval: 15_000,
		staleTime: 12_000,
	});

	return {
		balance: data?.balance,
		nativeBalance: data?.nativeBalance,
		formatted: data?.balance !== undefined ? formatBalance(data.balance, decimals) : "0",
		isLoading: enabled ? isLoading : false,
		error: error instanceof Error ? error : null,
		refetch,
	};
}
